import {
  Button,
  Col,
  DatePicker,
  Form,
  message,
  Row,
  Skeleton,
  Typography,
} from "antd";
import { createSchemaFieldRule } from "antd-zod";
import moment, { Moment } from "moment";
import { z } from "zod";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQueryClient } from "@tanstack/react-query";

import {
  ApiPaths,
  Mandate,
} from "@/providers/smarta-banking/banking-api-client";
import { DISPLAY_DATE_FORMAT, SAVE_DATE_FORMAT } from "@/v1/constants";
import { LanguageCode } from "@/v1/i18n";
import type { IUser } from "@/v1/types";
import { api } from "../../client";
import { ConnectionSummaryCard } from "./ConnectionSummaryCard";
import { InstitutionSelectField } from "./InstitutionSelectField";
import type { EnqueuedMandate } from "../connections-overview/MandateTable";

type Props = {
  user: IUser;
  onCancel: () => void;
  onSuccess?: () => void;
};

const getTexts = (t: TFunction) => ({
  institution: {
    label: t("createConnectionForm.institution.label"),
    placeholder: t("createConnectionForm.institution.placeholder"),
    errors: {
      required: t("createConnectionForm.institution.errors.required"),
    },
  },
  historicalStartDate: {
    label: t("createConnectionForm.historicalStartDate.label"),
    placeholder: t("createConnectionForm.historicalStartDate.placeholder"),
    hint: (minDate: Moment, maxDate: Moment) =>
      t("createConnectionForm.historicalStartDate.hint", {
        minDate: minDate.format(DISPLAY_DATE_FORMAT),
        maxDate: maxDate.format(DISPLAY_DATE_FORMAT),
      }),
    errors: {
      required: t("createConnectionForm.historicalStartDate.errors.required"),
      betweenDateRange: (minDate: Moment, maxDate: Moment) =>
        t("createConnectionForm.historicalStartDate.errors.betweenDateRange", {
          minDate: minDate.format(DISPLAY_DATE_FORMAT),
          maxDate: maxDate.format(DISPLAY_DATE_FORMAT),
        }),
    },
  },
  actions: {
    cancel: t("createConnectionForm.actions.cancel"),
    submit: t("createConnectionForm.actions.submit"),
  },
  messages: {
    institutionDetailsMissing: t(
      "createConnectionForm.messages.institutionDetailsMissing"
    ),
    connectionCreationStarted: t(
      "createConnectionForm.messages.connectionCreationStarted"
    ),
  },
});

export const CreateConnectionForm = ({ user, onCancel, onSuccess }: Props) => {
  const queryClient = useQueryClient();

  const { t } = useTranslation("Banking");
  const texts = getTexts(t);
  const maxHistoricalStartDate = moment().subtract(1, "days");
  const [minHistoricalStartDate, setMinHistoricalStartDate] = useState<Moment>(
    maxHistoricalStartDate
  );
  const isHistoricalStartDateValid = (historicalStartDate?: Moment) => {
    return (
      historicalStartDate?.isBetween(
        minHistoricalStartDate,
        maxHistoricalStartDate,
        "days",
        // "[]" means both the start date and the end date are inclusive
        "[]"
      ) ?? false
    );
  };

  const formValuesSchema = z.object({
    institutionId: z.string().min(1, texts.institution.errors.required),
    historicalStartDate: z
      .any()
      .refine(
        (date) => moment.isMoment(date),
        texts.historicalStartDate.errors.required
      )
      .refine(
        (date) => isHistoricalStartDateValid(date),
        texts.historicalStartDate.errors.betweenDateRange(
          minHistoricalStartDate,
          maxHistoricalStartDate
        )
      ),
  });
  const [form] = Form.useForm<z.infer<typeof formValuesSchema>>();
  const rule = createSchemaFieldRule(formValuesSchema);
  const institutionId = Form.useWatch("institutionId", form);
  const historicalStartDate = Form.useWatch<Moment | undefined>(
    "historicalStartDate",
    form
  );

  // queries & mutations
  const { data: institutionDetails, isLoading: isInstitutionDetailsLoading } =
    api.useQuery(
      "get",
      ApiPaths.getApiInstitutionsByInstitutionId,
      {
        params: {
          path: {
            institutionId,
          },
        },
      },
      {
        enabled: !!institutionId,
      }
    );

  const { mutate: createMandate, isPending } = api.useMutation(
    "post",
    ApiPaths.postApiMandates,
    {
      onSuccess: async () => {
        message.success(texts.messages.connectionCreationStarted);
        onSuccess?.();
        // add temporary entry to GET mandates query
        // with enqueued state
        const queryKey = [
          "get",
          ApiPaths.getApiMandatesByCustomerId,
          { params: { path: { customerId: user.publicId } } },
        ];

        queryClient.setQueryData<{ data: (Mandate | EnqueuedMandate)[] }>(
          queryKey,
          (oldData) => {
            const createdAt = new Date().toISOString();
            return {
              ...oldData,
              data: [
                ...(oldData?.data ?? []),
                {
                  type: "mandate",
                  createdAt,
                  updatedAt: createdAt,
                  institutionId,
                  customerId: user.publicId,
                  reference: `temp-${Date.now()}`,
                  state: "enqueued",
                },
              ],
            };
          }
        );
      },
      onError: (error) => {
        message.error(error.error.message);
      },
    }
  );

  // methods
  const handleSubmit = (values: z.infer<typeof formValuesSchema>) => {
    const validatedValues = formValuesSchema.parse(values);
    const historicalStartDate = moment(validatedValues.historicalStartDate);
    const maxHistoricalDays = Math.max(
      moment().diff(historicalStartDate, "days"),
      90
    );
    const institution = institutionDetails?.data;
    if (!institution) {
      message.error(texts.messages.institutionDetailsMissing);
      return;
    }

    createMandate({
      body: {
        institutionId: validatedValues.institutionId,
        customerId: user.publicId,
        customerLanguage: user.language ?? LanguageCode.GERMAN,
        customerDisplayName: user.firstName,
        customerEmail: user.email,
        accessScope: ["transactions", "details", "balances"],
        maxHistoricalDays,
        accountSelection:
          institution.supported_features.includes("account_selection"),
        accessValidForDays: institution.max_access_valid_for_days ?? 90,
        historicalStartDate: historicalStartDate.format(SAVE_DATE_FORMAT),
      },
    });
  };

  // effects
  useEffect(() => {
    if (!institutionDetails?.data) {
      return;
    }
    const transactionTotalDays =
      institutionDetails.data.transaction_total_days ?? 90;
    const oldestHistoricalStartDate = moment().subtract(
      transactionTotalDays,
      "days"
    );
    setMinHistoricalStartDate(() => oldestHistoricalStartDate);
    /*
     * Pre-select the oldest date the institution supports (still editable), so ops
     * fetch as much history as possible by default. This lets a customer later add
     * an earlier tax year without a re-sync. See SMT-1090.
     *
     * Only prefill when the field is still empty and untouched, so a manual edit is
     * never overwritten if the institution details are refetched. On an institution
     * switch the field is reset (below), which clears the touched state and lets the
     * new institution's default apply.
     */
    if (
      !form.isFieldTouched("historicalStartDate") &&
      !form.getFieldValue("historicalStartDate")
    ) {
      form.setFieldsValue({ historicalStartDate: oldestHistoricalStartDate });
    }
  }, [form, institutionDetails?.data]);

  // reset historicalStartDate while switching institution (re-filled once the new
  // institution's details load, in the effect above)
  useEffect(() => {
    form.resetFields(["historicalStartDate"]);
  }, [form, institutionId]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onReset={onCancel}
      disabled={isPending}
    >
      <InstitutionSelectField
        rules={[rule]}
        name="institutionId"
        label={texts.institution.label}
        placeholder={texts.institution.placeholder}
      />

      {institutionId && (
        <>
          <Form.Item
            name="historicalStartDate"
            label={texts.historicalStartDate.label}
            required
            rules={[rule]}
            extra={
              <Typography.Paragraph type="secondary" className="mt-1 mb-0">
                {texts.historicalStartDate.hint(
                  minHistoricalStartDate,
                  maxHistoricalStartDate
                )}
              </Typography.Paragraph>
            }
          >
            {isInstitutionDetailsLoading ? (
              <Skeleton.Button active block />
            ) : (
              <DatePicker
                style={{ width: "100%" }}
                format={DISPLAY_DATE_FORMAT}
                placeholder={texts.historicalStartDate.placeholder}
                disabledDate={(current) =>
                  current && !isHistoricalStartDateValid(current)
                }
              />
            )}
          </Form.Item>
          <ConnectionSummaryCard
            isLoading={isInstitutionDetailsLoading}
            institution={institutionDetails?.data}
            historicalStartDate={historicalStartDate}
          />
        </>
      )}

      <Row justify="end" gutter={8} className="mt-4">
        <Col>
          <Button type="default" htmlType="reset">
            {texts.actions.cancel}
          </Button>
        </Col>
        <Col>
          <Button type="primary" htmlType="submit">
            {texts.actions.submit}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};
